
import { SyntheticEvent } from 'react';
import { AutocompleteEpisode, AutocompleteItem, AutocompleteSeason, MediaType } from '@shared/types';
import { extractYoutubeVideoId, isYoutubeShow } from '@shared/utils/show';
import './MediaInfo.scss'

interface MediaInfoProps {
  media: AutocompleteItem;
  season?: AutocompleteSeason | null;
  episode?: AutocompleteEpisode | null;
  seasonNum?: number | null;
  episodeNum?: number | null;
  progressPercent?: number;
  isLoading?: boolean;
  short?: boolean;
  className?: string;
}

export const TRANSPARENT_POSTER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAALkAAAESAQMAAACbz+aGAAAAA1BMVEX///+nxBvIAAAAAXRSTlMAQObYZgAAAB1JREFUeNrtwTEBAAAAwiD7p7bGDmAAAAAAAACQchrCAAHyrvV6AAAAAElFTkSuQmCC';

const MediaInfo = ({ media, season, episode, seasonNum, episodeNum, progressPercent, isLoading = false, className, short }: MediaInfoProps) => {
  void short;
  const handleOnPosterError = (e: SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src=TRANSPARENT_POSTER;
    (e.target as HTMLImageElement).className = 'no-poster';
  };

  const resolvedSeasonNum = season?.num ?? seasonNum;
  const resolvedEpisodeNum = episode?.num ?? episodeNum;
  const hasEpisodeMeta = typeof resolvedSeasonNum === 'number' && typeof resolvedEpisodeNum === 'number';
  const isYoutube = isYoutubeShow(media);
  const youtubeVideoId = extractYoutubeVideoId(media);
  const fallbackYoutubePoster = youtubeVideoId
    ? `https://i.ytimg.com/vi/${youtubeVideoId}/hqdefault.jpg`
    : 'https://www.youtube.com/s/desktop/f506bd45/img/favicon_144x144.png';
  const posterSrc = media.poster || (isYoutube ? fallbackYoutubePoster : TRANSPARENT_POSTER);
  const displayTitle = (() => {
    const raw = `${media.originalTitle || media.title || ''}`.trim();
    if (!isYoutube) {
      return raw;
    }
    if (/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i.test(raw)) {
      return 'Video';
    }
    const cleaned = raw.replace(/^youtube:\s*/i, '').trim();
    return cleaned || 'Video';
  })();
  const progressHue = typeof progressPercent === 'number'
    ? Math.max(0, Math.min(120, Math.round((progressPercent / 100) * 120)))
    : 0;
  const progressFillStyle = typeof progressPercent === 'number'
    ? {
      width: `${Math.max(2, progressPercent)}%`,
      backgroundColor: `hsl(${progressHue} 78% 42%)`
    }
    : undefined;
  const metaTag = hasEpisodeMeta
    ? `S${resolvedSeasonNum.toString().padStart(2, '0')}E${resolvedEpisodeNum.toString().padStart(2, '0')}`
    : (isYoutube ? 'YOUTUBE' : 'MOVIE');
  const metaTail = hasEpisodeMeta ? (episode?.title || '') : '';

  return (
    <div className={'media-info' + (className ? ` ${className}` : '')}>
      <div className="poster-wrapper">
        <img
          src={posterSrc}
          className={(media.poster || isYoutube) ? '' : 'no-poster'}
          onError={handleOnPosterError}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
        />
        {isLoading && (
          <span className="loading-badge" title="Words are still loading for this series">
            <i className="ic ic-arrow-repeat" />
          </span>
        )}
      </div>
      <dl>
        <dt>
          {displayTitle}
          <span className="year-badge">{media.year}</span>
        </dt>
        {media.type === MediaType.MOVIE || hasEpisodeMeta ? (
          <dd className="meta-line">
            <span className="meta-tag">{metaTag}</span>
            {!!metaTail && <span className="meta-tail">{metaTail}</span>}
          </dd>
        ) : null}
        {typeof progressPercent === 'number' && (
          <dd className="progress-line" title="Learning progress">
            <span className="progress-top">
              <span className="progress-label">Progress</span>
              <span className="progress-value">{progressPercent}%</span>
            </span>
            <span className="progress-track">
              <span className="progress-fill" style={progressFillStyle} />
            </span>
          </dd>
        )}
        {isLoading && (
          <dd className="loading-line">Loading words...</dd>
        )}
      </dl>
    </div>
  );
};

export default MediaInfo;
